import { useCallback, useEffect, useState } from "react";
import Sortable from "../../../models/interfaces/sortable";
import { RecordUtils } from "@rsm-hcd/javascript-core";

export interface UseSortedRecords<T extends Sortable> {
    onRecordsMoved: (updatedItems: T[]) => SortItemMovement[];
    records: T[];
    resetRecordSort: () => number[];
}

export interface SortItemMovement {
    movingRecordId: number;
    precedingRecordId?: number;
}

export const useSortedRecords = <T extends Sortable>(
    initialRecords: T[],
    recordType: new () => T
): UseSortedRecords<T> => {
    const [records, setRecords] = useState<T[]>([]);
    const [priorRecordState, setPriorRecordState] = useState<T[]>([]);

    const onRecordsMoved = useCallback(
        (updatedObjects: T[]): SortItemMovement[] => {
            const updatedRecords = RecordUtils.ensureRecords(
                updatedObjects,
                recordType
            );

            setRecords(updatedRecords);

            const diffs = diffSortItems(records, updatedRecords);

            return diffs;
        },
        [recordType, records]
    );

    const resetRecordSort = useCallback((): number[] => {
        setRecords(priorRecordState);

        return priorRecordState.filter((r) => r.id != null).map((r) => r.id!);
    }, [priorRecordState]);

    useEffect(() => {
        setRecords(initialRecords);
    }, [initialRecords]);

    useEffect(() => {
        setPriorRecordState(records);
    }, [records]);

    return {
        onRecordsMoved: onRecordsMoved,
        records: records,
        resetRecordSort: resetRecordSort,
    };
};

const diffSortItems = <T extends Sortable>(
    original: T[],
    updated: T[]
): SortItemMovement[] => {
    const movements: SortItemMovement[] = [];
    const idsWithEnqueuedMoves: Set<number> = new Set();
    const originalIds = original.map((item) => item.id!);
    const updatedIds = updated.map((item) => item.id!);
    let precedingUpdatedId: number | undefined;

    while (updatedIds.length > 0) {
        const currentOriginalId = originalIds[0];
        const currentUpdatedId = updatedIds[0];

        if (idsWithEnqueuedMoves.has(currentUpdatedId)) {
            precedingUpdatedId = updatedIds.shift();
            continue;
        }

        if (currentOriginalId === currentUpdatedId) {
            precedingUpdatedId = updatedIds.shift();
            originalIds.shift();
            continue;
        }

        const indexOfCurrentOriginalIdInUpdatedIds =
            updatedIds.indexOf(currentOriginalId);
        const indexOfCurrentUpdatedIdInOriginalIds =
            originalIds.indexOf(currentUpdatedId);

        if (
            indexOfCurrentOriginalIdInUpdatedIds >
            indexOfCurrentUpdatedIdInOriginalIds
        ) {
            const movement: SortItemMovement = {
                movingRecordId: currentOriginalId,
            };

            if (indexOfCurrentOriginalIdInUpdatedIds > 0) {
                movement.precedingRecordId =
                    updatedIds[indexOfCurrentOriginalIdInUpdatedIds - 1];
            }

            movements.push(movement);
            idsWithEnqueuedMoves.add(currentOriginalId);
            originalIds.shift();
        } else {
            const movement: SortItemMovement = {
                movingRecordId: currentUpdatedId,
                precedingRecordId: precedingUpdatedId,
            };

            movements.push(movement);
            idsWithEnqueuedMoves.add(currentUpdatedId);

            updatedIds.shift();
            originalIds.splice(indexOfCurrentUpdatedIdInOriginalIds, 1);
        }
    }

    return movements;
};
